import { h, resolveComponent } from 'vue'
import { createRouter, createWebHashHistory } from 'vue-router'
import Layout from '@/components/Layout.vue'
import { usePermissionStore } from '@/stores/permission'
import { useCsNotifyStore } from '@/stores/cs-notify'

const ParentView = {
  render: () => h(resolveComponent('router-view'))
}

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
    hidden: true
  },
  {
    path: '/',
    name: 'RootLayout',
    component: Layout,
    redirect: '/home',
    children: [
      {
        path: '/home',
        name: 'Home',
        component: () => import('@/views/dashboard/index.vue'),
        meta: {
          title: '首页',
          icon: ''
        }
      },
      {
        path: '/profile',
        name: 'Profile',
        component: () => import('@/views/profile/index.vue'),
        meta: {
          title: '个人中心',
          icon: '',
          hidden: true
        }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/404.vue'),
    hidden: true
  }
]

const router = createRouter({
  // Use hash mode so direct access/refresh does not depend on gateway or ingress fallback rules.
  history: createWebHashHistory(),
  routes
})

let dynamicRoutesAdded = false

const viewMap = {
  'views/wxq/events': () => import('@/views/wxq/events.vue'),
  'views/wxq/config': () => import('@/views/wxq/config.vue'),
  'views/wxq/report': () => import('@/views/wxq/report.vue'),
  'views/wxq/simulation': () => import('@/views/wxq/simulation.vue'),
  'views/wxq/retention': () => import('@/views/wxq/retention.vue'),
  'views/friends/config': () => import('@/views/friends/config.vue'),
  'views/friends/gifts': () => import('@/views/friends/gifts.vue'),
  'views/user/list': () => import('@/views/user/list.vue'),
  'views/user/online': () => import('@/views/user/online.vue'),
  'views/system/user': () => import('@/views/system/user.vue'),
  'views/system/role': () => import('@/views/system/role.vue'),
  'views/system/role-perm': () => import('@/views/system/role-perm.vue'),
  'views/system/operation-log': () => import('@/views/system/operation-log.vue'),
  'views/system/game-switch': () => import('@/views/system/game-switch.vue'),
  'views/game/chat-config': () => import('@/views/game/chat-config.vue'),
  'views/game/vip-config': () => import('@/views/game/vip-config.vue'),
  'views/game/merchant-agent-config': () => import('@/views/game/merchant-agent-config.vue'),
  'views/game/merchant-announcements': () => import('@/views/game/merchant-announcements.vue'),
  'views/game/home-activity-slots': () => import('@/views/game/home-activity-slots.vue'),
  'views/game/mail': () => import('@/views/game/mail.vue'),
  'views/sys/oss-config': () => import('@/views/sys/oss-config.vue'),
  'views/sys/official-link': () => import('@/views/sys/official-link.vue'),
  'views/cs/channel': () => import('@/views/cs/channel.vue'),
  'views/cs/session': () => import('@/views/cs/session.vue'),
  'views/finance/topup-orders': () => import('@/views/finance/topup-orders.vue'),
  'views/finance/withdraw-orders': () => import('@/views/finance/withdraw-orders.vue'),
  'views/lottery/config': () => import('@/views/lottery/config.vue'),
  'views/lottery/records': () => import('@/views/lottery/records.vue'),
	'views/lottery/goods': () => import('@/views/lottery/goods.vue'),
	'views/lottery/stock': () => import('@/views/lottery/stock.vue'),
	'views/lottery/orders': () => import('@/views/lottery/orders.vue'),
	'views/lottery/reports': () => import('@/views/lottery/reports.vue'),
	'views/lottery/audit': () => import('@/views/lottery/audit.vue'),
  'views/rank/config': () => import('@/views/rank/config.vue'),
  'views/rank/prizes': () => import('@/views/rank/prizes.vue'),
  'views/rank/bots': () => import('@/views/rank/bots.vue'),
  'views/rank/bot-crawl-slots': () => import('@/views/rank/bot-crawl-slots.vue'),
  'views/rank/snapshots': () => import('@/views/rank/snapshots.vue'),
  'views/game-stats/qk-block': () => import('@/views/game-stats/qk-block.vue'),
  'views/game-stats/qk-baccarat': () => import('@/views/game-stats/qk-baccarat.vue'),
  'views/game-stats/qk-niuniu': () => import('@/views/game-stats/qk-niuniu.vue'),
  'views/placeholder/index': () => import('@/views/placeholder/index.vue')
}

const pathViewMap = {
  '/wxq/events': () => import('@/views/wxq/events.vue'),
  '/wxq/config': () => import('@/views/wxq/config.vue'),
  '/wxq/report': () => import('@/views/wxq/report.vue'),
  '/wxq/simulation': () => import('@/views/wxq/simulation.vue'),
  '/wxq/retention': () => import('@/views/wxq/retention.vue'),
  '/friends/config': () => import('@/views/friends/config.vue'),
  '/friends/gifts': () => import('@/views/friends/gifts.vue'),
  '/user/list': () => import('@/views/user/list.vue'),
  '/user/online': () => import('@/views/user/online.vue'),
  '/system/user': () => import('@/views/system/user.vue'),
  '/system/role': () => import('@/views/system/role.vue'),
  '/system/role-perm': () => import('@/views/system/role-perm.vue'),
  '/system/operation-log': () => import('@/views/system/operation-log.vue'),
  '/system/game-switch': () => import('@/views/system/game-switch.vue'),
  '/game/chat-config': () => import('@/views/game/chat-config.vue'),
  '/game/vip-config': () => import('@/views/game/vip-config.vue'),
  '/game/merchant-agent-config': () => import('@/views/game/merchant-agent-config.vue'),
  '/game/merchant-announcements': () => import('@/views/game/merchant-announcements.vue'),
  '/game/home-activity-slots': () => import('@/views/game/home-activity-slots.vue'),
  '/game/mail': () => import('@/views/game/mail.vue'),
  '/sys/oss-config': () => import('@/views/sys/oss-config.vue'),
  '/sys/official-link': () => import('@/views/sys/official-link.vue'),
  '/cs/channel': () => import('@/views/cs/channel.vue'),
  '/cs/session': () => import('@/views/cs/session.vue'),
  '/finance/topup-orders': () => import('@/views/finance/topup-orders.vue'),
  '/finance/withdraw-orders': () => import('@/views/finance/withdraw-orders.vue'),
  '/lottery/config': () => import('@/views/lottery/config.vue'),
  '/lottery/records': () => import('@/views/lottery/records.vue'),
	'/lottery/goods': () => import('@/views/lottery/goods.vue'),
	'/lottery/stock': () => import('@/views/lottery/stock.vue'),
	'/lottery/orders': () => import('@/views/lottery/orders.vue'),
	'/lottery/reports': () => import('@/views/lottery/reports.vue'),
	'/lottery/audit': () => import('@/views/lottery/audit.vue'),
  '/rank/config': () => import('@/views/rank/config.vue'),
  '/rank/prizes': () => import('@/views/rank/prizes.vue'),
  '/rank/bots': () => import('@/views/rank/bots.vue'),
  '/rank/bot-crawl-slots': () => import('@/views/rank/bot-crawl-slots.vue'),
  '/rank/snapshots': () => import('@/views/rank/snapshots.vue'),
  '/game-stats/qk-block': () => import('@/views/game-stats/qk-block.vue'),
  '/game-stats/qk-baccarat': () => import('@/views/game-stats/qk-baccarat.vue'),
  '/game-stats/qk-niuniu': () => import('@/views/game-stats/qk-niuniu.vue'),
}

function normalizeComponentPath(component) {
  if (!component) return ''
  return component.replace(/^\/+/, '').replace(/\.vue$/, '')
}

function resolveRouteComponent(menu, hasChildren) {
  const componentPath = normalizeComponentPath(menu.component || '')
  // 动态路由一律挂在 RootLayout（已是整站壳）下；菜单里的 LAYOUT 表示「带子路由的目录」，
  // 必须用 ParentView，否则会再嵌套一套 Layout，出现双侧边栏/双面包屑。
  if (componentPath.toUpperCase() === 'LAYOUT') {
    return ParentView
  }
  const normalizedPath = (menu.path || '').startsWith('/') ? menu.path : `/${menu.path || ''}`
  if (!componentPath && pathViewMap[normalizedPath]) {
    return pathViewMap[normalizedPath]
  }
  if (!componentPath && hasChildren) {
    return ParentView
  }
  return viewMap[componentPath] || (() => import('@/views/placeholder/index.vue'))
}

function transformMenuToRoute(menu) {
  const children = (menu.children || []).map(transformMenuToRoute).filter(Boolean)
  const component = resolveRouteComponent(menu, children.length > 0)
  const path = menu.path || `/${menu.menuCode || menu.id}`

  const route = {
    path,
    name: menu.routeName || menu.menuCode || `menu_${menu.id?.replaceAll('-', '_')}`,
    component,
    redirect: menu.redirect || undefined,
    hidden: !!menu.hidden,
    meta: {
      title: menu.metaTitle || menu.menuName,
      icon: menu.metaIcon || menu.icon || '',
      perm: menu.perm || ''
    }
  }
  if (children.length > 0) {
    route.children = children
  }
  return route
}

function addDynamicRoutes(menuTree) {
  const routeList = (menuTree || []).map(transformMenuToRoute).filter(Boolean)
  routeList.forEach(route => {
    if (!router.hasRoute(route.name)) {
      router.addRoute('RootLayout', route)
    }
  })
  return routeList
}

function resolveFirstMenuPath(routeList = []) {
  for (const route of routeList) {
    if (!route) continue
    if (route.path && route.path !== '/') {
      return route.path.startsWith('/') ? route.path : `/${route.path}`
    }
    if (Array.isArray(route.children) && route.children.length > 0) {
      const childPath = resolveFirstMenuPath(route.children)
      if (childPath) {
        return childPath
      }
    }
  }
  return ''
}

router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem('token')
  const permissionStore = usePermissionStore()
  if (!permissionStore.loaded) {
    dynamicRoutesAdded = false
  }

  if (to.path === '/login') {
    if (token) {
      try {
        if (!dynamicRoutesAdded) {
          await permissionStore.loadAuthInfo()
          addDynamicRoutes(permissionStore.menuTree)
          dynamicRoutesAdded = true
          return next('/home')
        }
        return next('/home')
      } catch (e) {
        try {
          useCsNotifyStore().teardown()
        } catch (_) {}
        localStorage.removeItem('token')
        permissionStore.reset()
        return next('/login')
      }
    } else {
      return next()
    }
  } else {
    if (token) {
      if (!dynamicRoutesAdded) {
        try {
          await permissionStore.loadAuthInfo()
          addDynamicRoutes(permissionStore.menuTree)
          dynamicRoutesAdded = true
          if (to.path === '/') {
            return next('/home')
          }
          // On refresh, `to` may already be matched as NotFound before dynamic routes are added.
          // Redirect by path/fullPath to force rematch instead of reusing stale route name.
          return next({ path: to.fullPath, replace: true })
        } catch (e) {
          console.error('加载动态路由失败:', e)
          try {
            useCsNotifyStore().teardown()
          } catch (_) {}
          localStorage.removeItem('token')
          permissionStore.reset()
          return next('/login')
        }
      }
      if (to.path === '/') {
        return next('/home')
      }
      return next()
    } else {
      return next('/login')
    }
  }
})

export default router
